import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { type SortingState, useTable } from '@tanstack/react-table';
import { Building2, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Organization } from '@/common/organization';
import { AbsoluteLoader } from '@/components/absoluteLoader';
import { DataTable } from '@/components/data-table/data-table';
import { toSortParam } from '@/components/data-table/sort-param';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import { updateOrganizationOptions } from '@/queries/organizations/update-orgnization';
import { queryClient } from '@/queries/query.client';
import { ORGANIZATION_SORT_FIELDS, organizationColumns, organizationTableFeatures } from './columns';
import { OrganizationDrawer } from './OrganizationDrawer';

type StatusFilter = 'all' | 'active' | 'inactive';

export function OrganizationsTab() {
	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [isOrgDrawerOpen, setIsOrgDrawerOpen] = useState(false);
	const [sorting, setSorting] = useState<SortingState>([]);
	const {
		data: listOrganizationResponse,
		refetch,
		isLoading,
		isRefetching,
	} = useQuery({
		...getListOrganizationsOption({ sort: toSortParam(sorting, ORGANIZATION_SORT_FIELDS) }),
		// Keep the current rows on screen while a new sort order loads.
		placeholderData: keepPreviousData,
	});
	const organizations = listOrganizationResponse?.organizations || [];
	const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

	const filteredOrganizations = organizations.filter((org) => {
		const matchesSearch = org?.name?.toLowerCase().includes(searchQuery.toLowerCase());
		const matchesStatus = statusFilter === 'all' || (statusFilter === 'active') === (org.enabled !== false);
		return matchesSearch && matchesStatus;
	});

	const updateOrganizationApi = useMutation(updateOrganizationOptions());

	const handleEditClick = (org: Organization) => {
		setSelectedOrganization(org);
		setIsOrgDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setIsOrgDrawerOpen(false);
		setSelectedOrganization(null);
	};

	const handleCreateClick = () => {
		setSelectedOrganization(null);
		setIsOrgDrawerOpen(true);
	};

	const handleToggleStatus = async (organization: Organization, enabled: boolean) => {
		await updateOrganizationApi.mutateAsync({
			id: organization.id as string,
			payload: { enabled },
		});
		// invalidate the all assets, inspections and other related queries to reflect the updated organization status
		queryClient.invalidateQueries({ queryKey: ['assets'] });
		refetch();
	};

	const handleOrgChange = () => {
		refetch();
	};
	const table = useTable({
		features: organizationTableFeatures,
		columns: organizationColumns,
		data: filteredOrganizations,
		getRowId: (row) => row.id as string,
		state: { sorting },
		onSortingChange: setSorting,
		// The API sorts the list; search and status filters then narrow it client-side.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			isUpdating: updateOrganizationApi.isPending,
			onToggleStatus: handleToggleStatus,
			onEdit: handleEditClick,
		},
	});

	const showLoader = isLoading || isRefetching || updateOrganizationApi.isPending;

	return (
		<>
			<div className="space-y-4">
				{/* Header with Search and Create Button */}
				<div className="flex items-center gap-4">
					<div className="relative flex-1">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							type="text"
							placeholder={t('admin.organizations.searchPlaceholder')}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-10 pr-4 py-2 bg-input"
						/>
					</div>
					<Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
						<SelectTrigger className="w-40 bg-input" aria-label={t('admin.organizations.statusFilter.ariaLabel')}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('admin.organizations.statusFilter.all')}</SelectItem>
							<SelectItem value="active">{t('common.states.active')}</SelectItem>
							<SelectItem value="inactive">{t('common.states.inactive')}</SelectItem>
						</SelectContent>
					</Select>
					<Button onClick={handleCreateClick}>
						<Plus className="size-4 mr-2" />
						{t('admin.organizations.create')}
					</Button>
				</div>
				<div className="relative">
					<AbsoluteLoader isLoading={showLoader} />

					{/* Organizations Table */}
					{!isLoading && filteredOrganizations.length > 0 && <DataTable table={table} />}
					{!isLoading && !filteredOrganizations.length && (
						<Card>
							<CardContent className="p-12">
								<div className="text-center">
									<div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4">
										<Building2 className="h-6 w-6 text-muted-foreground" />
									</div>
									<h3 className="text-lg font-semibold mb-2">{t('admin.organizations.empty.title')}</h3>
									<p className="text-sm text-muted-foreground mb-4">
										{searchQuery
											? t('admin.organizations.empty.searchHint')
											: t('admin.organizations.empty.createHint')}
									</p>
									{!searchQuery && (
										<Button onClick={handleCreateClick}>
											<Plus className="size-4 mr-2" />
											{t('admin.organizations.create')}
										</Button>
									)}
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			</div>

			<OrganizationDrawer
				open={isOrgDrawerOpen}
				onOpenChange={handleCloseDrawer}
				organization={selectedOrganization}
				onOrgChange={handleOrgChange}
			/>
		</>
	);
}
